
import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom'
import DimOverlay from '@marketplace/components/dim-overlay.vue'
import CustomTooltip from '@marketplace/components/pulse/custom-tooltip.vue'
import PulseIndicator from '@marketplace/components/pulse/pulseIndicator.vue'
import { MARK_EDUCATIONAL_PULSE_SEEN } from '@marketplace/graphql/educationPulses.js'
import { trackTooltip } from '@marketplace/helpers/segment/index.js'
import { EventBus } from '@marketplace/plugins/event-bus.js'

export default {
    name: 'PulsePulseTooltipWrapper',

    components: {
        CustomTooltip,
        PulseIndicator,
        DimOverlay,
    },

    props: {
        tooltipLabel: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        placement: {
            type: String,
            default: 'bottom',
        },
        trackStringId: {
            type: String,
            default: null,
        },
        // Must be available/added in account_educational_pulses.php
        pulseStringIds: {
            type: Array,
            default: () => [],
        },
        hasDefaultTooltipFallback: {
            type: Boolean,
            default: false,
        },
        tooltipWidth: {
            type: Number,
            default: 250,
        },
        defaultTooltipContent: {
            type: String,
            default: '',
        },
        id: {
            type: String,
            required: true,
        },
        classOverride: {
            type: String,
            default: '',
        },

        hideOverlay: {
            type: Boolean,
            default: false,
        },
        forceHidePulse: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            hasError: false,
            isHidden: true,
            pulseSeen: false,
            tooltipToggle: false,
            seenPulseIds: [],
        }
    },

    computed: {
        isMobile() {
            return this.$breakpoint.includes('mobile')
        },
        canPulseShown() {
            if (this.forceHidePulse) {
                return false
            }
            return this.$helpers.isLoggedIn && !this.pulseSeen
        },
        getContent() {
            return this.hasDefaultTooltipFallback
                ? this.defaultTooltipContent || this.tooltipLabel
                : this.tooltipLabel
        },
        canShow() {
            return this.canPulseShown || this.hasDefaultTooltipFallback
        },
        hasFallbackIcon() {
            return this.pulseSeen && this.hasDefaultTooltipFallback
        },
    },

    watch: {
        isHidden(newValue, oldValue) {
            if (!newValue && oldValue !== newValue) {
                if (this.trackStringId && this.canPulseShown) {
                    trackTooltip(this.trackStringId)
                    this.markPulseAsSeen()
                }

                if (!newValue) {
                    this.calculatePosition()
                }
            }
        },

        canPulseShown(newVal, oldVal) {
            if (newVal !== oldVal) {
                EventBus.$emit('pulse-seen', this.canPulseShown)
            }
        },
    },

    mounted() {
        this.$nextTick(() => {
            try {
                this.pulseStringIds.forEach((pulseId) => {
                    EventBus.$on(
                        this.id + ':pulseSeenChanged:' + pulseId,
                        (value) => {
                            this.pulseSeen = value
                        }
                    )
                })
                window.addEventListener('resize', this.handleResize)
                window.addEventListener('popstate', this.hide)
                document.addEventListener('keydown', this.handleEscKey)
            } catch (error) {
                console.error('Error in mounted lifecycle hook:', error)
            }
            EventBus.$emit('pulse-seen', this.canPulseShown)
        })
    },

    beforeDestroy() {
        this.pulseStringIds.forEach((pulseId) => {
            EventBus.$off(this.id + ':pulseSeenChanged:' + pulseId)
        })
        window.removeEventListener('resize', this.handleResize)
        window.removeEventListener('popstate', this.hide)
        document.removeEventListener('keydown', this.handleEscKey)
    },

    beforeMount() {
        this.$refs.pulseTooltipRef = null
        this.$refs.referenceRef = null
    },

    methods: {
        clearHoverTimer() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer)
            }
        },
        handleResize() {
            if (!this.isHidden) {
                this.show()
            }
        },
        handleEscKey(e) {
            if (e.key === 'Escape' || e.key === 'Esc') {
                if (!this.isHidden) {
                    this.hide()
                }
            }
        },

        async calculatePosition() {
            // Check if tooltip is hidden
            if (this.isHidden) return
            await this.$nextTick()

            const arrowRef = this.$refs.pulseTooltipRef.getArrowRef()
            const floatingRef = this.$refs.pulseTooltipRef.getFloatingRef()
            const hiddenDivRef = this.$refs.pulseTooltipRef.getHiddenDivRef()
            if (
                arrowRef &&
                floatingRef &&
                hiddenDivRef &&
                this.$refs.referenceRef
            ) {
                const { x, y, middlewareData, placement } =
                    await computePosition(
                        this.$refs.referenceRef,
                        floatingRef,
                        {
                            placement: this.placement,
                            middleware: [
                                offset(8),
                                flip(),
                                shift({ padding: 5 }),
                                arrow({ element: arrowRef }),
                            ],
                        }
                    )

                Object.assign(floatingRef.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                })

                const { x: arrowX, y: arrowY } = middlewareData.arrow

                const mainPlacement = placement.split('-')[0]
                const opposedSide = {
                    top: 'bottom',
                    bottom: 'top',
                    left: 'right',
                    right: 'left',
                }[mainPlacement]

                Object.assign(arrowRef.style, {
                    left: arrowX ? `${arrowX}px` : '',
                    top: arrowY ? `${arrowY}px` : '',
                    bottom: '',
                    right: '',
                    [opposedSide]: '-4px',
                })

                // Adjust hiddenDivRef based on placement
                let extraStyle = {}
                switch (mainPlacement) {
                    case 'top':
                        extraStyle = {
                            height: '30px',
                            width: `${floatingRef.offsetWidth}px`,
                            bottom: '-30px',
                        }
                        break
                    case 'bottom':
                        extraStyle = {
                            height: '30px',
                            width: `${floatingRef.offsetWidth}px`,
                            top: '-30px',
                        }
                        break
                    case 'left':
                        extraStyle = {
                            height: `${floatingRef.offsetHeight}px`,
                            width: '30px',
                            right: '-30px',
                        }
                        break
                    case 'right':
                        extraStyle = {
                            height: `${floatingRef.offsetHeight}px`,
                            width: '30px',
                            left: '-30px',
                        }
                        break
                }

                Object.assign(hiddenDivRef.style, extraStyle)
            }
        },

        hide() {
            if (this.isHidden) {
                return
            }
            this.isHidden = true
            this.tooltipToggle = false

            this.$emit('tooltip-hidden')
        },

        show() {
            if (!this.isHidden) {
                return
            }

            if (this.mouseLeaveTimeout) {
                clearTimeout(this.mouseLeaveTimeout)
            }
            this.hoverTimer = setTimeout(async () => {
                this.isHidden = false
                this.tooltipToggle = true

                // Wait for Vue to update the DOM before calculating the position.
                await this.$nextTick()

                if (this.$refs.referenceRef && this.$refs.pulseTooltipRef) {
                    if (
                        typeof this.$refs.pulseTooltipRef.getFloatingRef ===
                        'function'
                    ) {
                        const floatingRef =
                            this.$refs.pulseTooltipRef.getFloatingRef()

                        if (floatingRef) {
                            if (this.isMobile) {
                                Object.assign(floatingRef.style, {
                                    position: 'fixed',
                                    bottom: '0',
                                    left: '0',
                                    right: '0',
                                })
                            } else {
                                // Reset the styles when not in mobile view.
                                Object.assign(floatingRef.style, {
                                    position: '',
                                    bottom: '',
                                    left: '',
                                    right: '',
                                })
                                this.calculatePosition()
                            }

                            this.$emit('tooltip-shown')
                        } else {
                            console.error(
                                'getFloatingRef() did not return a valid reference'
                            )
                        }
                    } else {
                        console.error(
                            'pulseTooltipRef does not have a getFloatingRef() method'
                        )
                    }
                } else {
                    console.error('DOM not yet available')
                    this.isHidden = true
                    this.tooltipToggle = false
                }
            }, 250)
        },

        handleMouseLeave() {
            this.clearHoverTimer()

            if (this.mouseLeaveTimeout) {
                clearTimeout(this.mouseLeaveTimeout)
            }
            this.mouseLeaveTimeout = setTimeout(() => {
                if (!this.isMobile) {
                    this.hide()
                }
            }, 250)
        },

        handleReferenceClick() {
            if (this.isHidden) {
                this.show()
            }
        },

        markPulseAsSeen() {
            Promise.all(
                this.pulseStringIds.map(async (pulseId) => {
                    if (this.seenPulseIds.includes(pulseId)) {
                        return Promise.resolve()
                    }
                    try {
                        const { data } = await this.$apollo.mutate({
                            mutation: MARK_EDUCATIONAL_PULSE_SEEN,
                            variables: {
                                pulseId,
                            },
                        })

                        const { userErrors } =
                            data?.markEducationalPulseSeen || {}
                        if (userErrors?.field || userErrors?.message) {
                            this.hasError = true
                        } else {
                            this.hasError = false
                            this.seenPulseIds.push(pulseId)
                        }
                    } catch (error) {
                        this.hasError = true
                    }
                })
            )
        },
    },
}
